import { format } from 'date-fns';
import { sk, enUS } from 'date-fns/locale';
import { forwardRef } from 'react';
import { getLocalizedStrings, formatCurrency as formatCurrencyLocalized } from "@shared/localization";

// Helper function to format currency
const formatCurrency = (amount: number, currency: 'EUR' | 'USD' = 'EUR') => {
  return formatCurrencyLocalized(amount, currency);
};

// Helper function to format date
const formatDate = (date: string | Date | null | undefined, market: string = 'SK') => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return market === 'US' 
      ? dateObj.toLocaleDateString('en-US')
      : dateObj.toLocaleDateString('sk-SK');
  } catch (e) {
    return date?.toString() || '-';
  }
};

interface CreditNotePreviewProps {
  creditNote: any;
  client: any;
  user: any;
  className?: string;
  printMode?: boolean;
}

export const CreditNotePreview = forwardRef<HTMLDivElement, CreditNotePreviewProps>(
  ({ creditNote, client, user, className = '', printMode = false }, ref) => {
    
    // Lokalizované stringy podľa user market
    const strings = getLocalizedStrings(user?.market || 'SK');
    const currency = user?.currency || 'EUR';
    
    // Primárna farba z profilu alebo default modrá
    const primaryColor = user?.invoicePrimaryColor || '#2563eb';
    
    // Normalize credit note data
    const creditNoteNumber = creditNote?.creditNoteNumber || creditNote?.credit_note_number || '';
    const issueDate = creditNote?.issueDate || creditNote?.issue_date;
    const relatedInvoiceNumber = creditNote?.invoice?.invoiceNumber || creditNote?.originalInvoiceNumber || creditNote?.invoice_number || '-';
    const status = creditNote?.status || 'draft';
    const reason = creditNote?.reason || '';
    const notes = creditNote?.notes || '';
    
    // Amount calculations
    const isVatPayer = user?.isVatPayer || false;
    const amount = parseFloat(creditNote?.amount?.toString() || '0') || 0;
    const vatAmount = parseFloat(creditNote?.vatAmount?.toString() || creditNote?.vat_amount?.toString() || '0') || 0;
    const totalAmount = parseFloat(creditNote?.totalAmount?.toString() || creditNote?.total_amount?.toString() || '0') || 0;
    const vatRate = parseFloat(creditNote?.vatRate?.toString() || user?.companyVatRate?.toString() || '20') || 20;
    
    // Pripravíme dáta pre template
    const addrCompany = [
      user?.companyStreet,
      user?.companyCity && user?.companyPostalCode ? `${user.companyCity} ${user.companyPostalCode}` : user?.companyCity,
      user?.companyCountry
    ].filter(Boolean).join(', ');
    
    const addrClient = [
      client?.businessName || client?.name,
      client?.street,
      client?.city && client?.postalCode ? `${client.city} ${client.postalCode}` : client?.city,
      client?.country
    ].filter(Boolean).join(', ');

    const creditNoteStyles = `
      .credit-note-template {
        --primary: ${primaryColor};
        --text: #4a5a6b;
        --muted: #6b7280;
        --line: #e5e7eb;
        --bg: #ffffff;
        --card: #f8fafc;
      }
      
      .credit-note-template * { 
        box-sizing: border-box; 
        color: var(--text);
      }
      
      .credit-note-template .page {
        max-width: 900px;
        margin: 0 auto;
        background: var(--bg);
        padding: 0;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, Arial;
        font-size: 14px;
        line-height: 1.45;
      }
      
      .credit-note-template .wrap { 
        padding: 32px 40px;
      }
      
      .credit-note-template .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 32px;
      }
      
      .credit-note-template .brand {
        display: flex;
        gap: 16px;
        align-items: flex-start;
      }
      
      .credit-note-template .brand img { 
        height: 20px; 
        object-fit: contain; 
      }
      
      .credit-note-template .brand-info .name { 
        font-weight: 700; 
        font-size: 18px; 
        color: var(--text);
        margin-bottom: 4px;
      }
      
      .credit-note-template .company-contact {
        text-align: right;
        font-size: 12px;
        color: var(--muted);
        line-height: 1.5;
      }
      
      .credit-note-template .company-contact > div {
        margin-bottom: 2px;
      }
      
      .credit-note-template .title {
        margin: 0 0 8px;
        font-size: 32px;
        letter-spacing: .05em;
        font-weight: 800;
        color: var(--primary);
        text-align: center;
      }
      
      .credit-note-template .credit-note-number {
        text-align: center;
        margin-bottom: 24px;
        font-size: 16px;
        font-weight: 600;
        color: var(--text);
      }
      
      .credit-note-template .info-section {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;
        margin-bottom: 32px;
      }
      
      .credit-note-template .info-box {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 8px;
        padding: 20px;
      }
      
      .credit-note-template .info-box .label {
        color: var(--muted);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: .06em;
        margin-bottom: 8px;
        font-weight: 600;
      }
      
      .credit-note-template .client-name {
        font-weight: 700;
        font-size: 16px;
        color: var(--text);
        margin-bottom: 8px;
      }
      
      .credit-note-template .address-line {
        font-size: 13px;
        color: var(--text);
        line-height: 1.4;
        margin-bottom: 2px;
      }
      
      .credit-note-template .dates-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        margin-top: 16px;
      }
      
      .credit-note-template .date-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      
      .credit-note-template .date-label {
        font-size: 11px;
        color: var(--muted);
        text-transform: uppercase;
        letter-spacing: .05em;
        font-weight: 600;
      }
      
      .credit-note-template .date-value {
        font-size: 13px;
        color: var(--text);
        font-weight: 600;
      }
      
      .credit-note-template .reason-section {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 24px;
      }
      
      .credit-note-template .reason-title {
        color: var(--muted);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: .06em;
        margin-bottom: 8px;
        font-weight: 600;
      }
      
      .credit-note-template .reason-text {
        font-size: 13px;
        color: var(--text);
        line-height: 1.6;
      }
      
      .credit-note-template .summary-section {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 32px;
      }
      
      .credit-note-template .summary-box {
        width: 400px;
        border: 1px solid var(--line);
        border-radius: 8px;
        overflow: hidden;
      }
      
      .credit-note-template .summary-grid {
        padding: 16px;
        background: white;
      }
      
      .credit-note-template .summary-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 13px;
      }
      
      .credit-note-template .summary-row:last-child {
        margin-bottom: 0;
      }
      
      .credit-note-template .summary-label {
        color: var(--muted);
        font-weight: 500;
      }
      
      .credit-note-template .summary-value {
        color: var(--text);
        font-weight: 600;
      }
      
      .credit-note-template .summary-value.negative {
        color: #dc2626;
      }
      
      .credit-note-template .total-bar {
        background: var(--primary);
        color: white;
        padding: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 700;
        font-size: 16px;
      }
      
      .credit-note-template .bottom-section {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;
        margin-bottom: 32px;
      }
      
      .credit-note-template .terms-box {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 8px;
        padding: 20px;
      }
      
      .credit-note-template .terms-title {
        font-size: 12px;
        color: var(--muted);
        text-transform: uppercase;
        letter-spacing: .06em;
        margin-bottom: 8px;
        font-weight: 600;
      }
      
      .credit-note-template .terms-content {
        font-size: 12px;
        line-height: 1.6;
        color: var(--text);
      }
      
      .credit-note-template .footer {
        background: #4a5a6b;
        color: #e5e7eb;
        padding: 16px 20px;
        font-size: 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 3px solid var(--primary);
        margin: 0 -40px -32px -40px;
      }
      
      @media print {
        .credit-note-template .page { 
          box-shadow: none; 
          margin: 0; 
          max-width: none;
        }
        .credit-note-template .wrap {
          padding: 20px;
        }
        .credit-note-template .footer {
          margin: 32px -20px -20px -20px;
        }
      }
    `;

    return (
      <div ref={ref} className={`credit-note-template ${className}`}>
        <style>{creditNoteStyles}</style>
        
        <div className="page">
          <div className="wrap">
            {/* HEADER */}
            <div className="header">
              <div className="brand">
                {user?.companyLogo && (
                  <img src={user.companyLogo} alt="Logo" />
                )}
                <div className="brand-info">
                  <div className="name">{user?.companyName || 'TaskkroW'}</div>
                </div>
              </div>
              
              <div className="company-contact">
                {user?.companyStreet && <div>{user.companyStreet}</div>}
                {user?.companyCity && (
                  <div>
                    {user.companyPostalCode && `${user.companyPostalCode} `}{user.companyCity}
                  </div>
                )}
                {user?.companyCountry && <div>{user.companyCountry}</div>}
                {user?.companyPhone && <div>Tel: {user.companyPhone}</div>}
                {user?.email && <div>E-mail: {user.email}</div>}
                {user?.companyWebsite && (
                  <div>
                    <a href={user.companyWebsite} target="_blank" rel="noopener noreferrer">
                      {user.companyWebsite.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="title">
              {user?.market === 'SK' ? 'DOBROPIS' : 'CREDIT NOTE'}
            </div>
            
            <div className="credit-note-number">
              {user?.market === 'SK' ? 'Číslo dobropisu' : 'Credit Note Number'}: {creditNoteNumber}
            </div>

            <div className="info-section">
              <div className="info-box">
                <div className="label">
                  {user?.market === 'SK' ? 'Odberateľ' : 'Bill to'}
                </div>
                <div className="client-name">
                  {client?.businessName || client?.name || '—'}
                </div>
                {client?.street && <div className="address-line">{client.street}</div>}
                {client?.city && (
                  <div className="address-line">
                    {client.postalCode && `${client.postalCode} `}{client.city}
                  </div>
                )}
                {client?.country && <div className="address-line">{client.country}</div>}
                {client?.email && <div className="address-line">{client.email}</div>}
                {(client?.businessId || client?.ico) && (
                  <div className="address-line">IČO: {client?.businessId || client?.ico}</div>
                )}
              </div>

              <div className="info-box">
                <div className="dates-grid">
                  <div className="date-item">
                    <div className="date-label">{user?.market === 'SK' ? 'Dátum vystavenia' : 'Issue Date'}</div>
                    <div className="date-value">{formatDate(issueDate, user?.market)}</div>
                  </div>
                  <div className="date-item">
                    <div className="date-label">{user?.market === 'SK' ? 'Súvisiaca faktúra' : 'Related Invoice'}</div>
                    <div className="date-value">{relatedInvoiceNumber}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* REASON SECTION */}
            {reason && (
              <div className="reason-section">
                <div className="reason-title">
                  {user?.market === 'SK' ? 'Dôvod dobropisu' : 'Credit Note Reason'}
                </div>
                <div className="reason-text">{reason}</div>
                {notes && (
                  <div className="reason-text" style={{ marginTop: '12px', fontStyle: 'italic', fontSize: '12px', color: 'var(--muted)' }}>
                    <strong>{user?.market === 'SK' ? 'Poznámky:' : 'Notes:'}</strong> {notes}
                  </div>
                )}
              </div>
            )}

            {/* SUMMARY SECTION */}
            <div className="summary-section">
              <div className="summary-box">
                <div className="summary-grid">
                  <div className="summary-row">
                    <div className="summary-label">{user?.market === 'SK' ? 'Suma bez DPH' : 'Amount without VAT'}</div>
                    <div className="summary-value negative">-{formatCurrency(Math.abs(amount), currency)}</div>
                  </div>
                  
                  {isVatPayer && vatAmount > 0 && (
                    <div className="summary-row">
                      <div className="summary-label">
                        {user?.market === 'SK' ? 'DPH' : 'VAT'} ({vatRate}%)
                      </div>
                      <div className="summary-value negative">-{formatCurrency(Math.abs(vatAmount), currency)}</div>
                    </div>
                  )}
                </div>
                
                <div className="total-bar">
                  <span>{user?.market === 'SK' ? 'Celkom' : 'Total'} {isVatPayer ? (user?.market === 'SK' ? 's DPH' : 'with VAT') : ''}</span>
                  <span>-{formatCurrency(Math.abs(isVatPayer ? totalAmount : amount), currency)}</span>
                </div>
              </div>
            </div>

            {/* BOTTOM SECTION */}
            <div className="bottom-section">
              <div className="terms-box">
                <div className="terms-title">{user?.market === 'SK' ? 'Platobné údaje' : 'Payment Details'}</div>
                <div className="terms-content">
                  {user?.bankIban && (
                    <div><strong>{user?.market === 'SK' ? 'IBAN:' : 'IBAN:'}</strong> {user.bankIban}</div>
                  )}
                  {user?.bankSwift && (
                    <div><strong>SWIFT:</strong> {user.bankSwift}</div>
                  )}
                  <div>
                    <strong>{user?.market === 'SK' ? 'Variabilný symbol:' : 'Variable Symbol:'}</strong> {creditNoteNumber?.replace(/\D/g, '') || '—'}
                  </div>
                </div>
              </div>

              <div className="terms-box">
                <div className="terms-title">{user?.market === 'SK' ? 'Podmienky' : 'Terms'}</div>
                <div className="terms-content">
                  {user?.market === 'SK' ? (
                    <div>Dobropis k faktúre č. {relatedInvoiceNumber}</div>
                  ) : (
                    <div>Credit note for invoice no. {relatedInvoiceNumber}</div>
                  )}
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="footer">
              <div>{user?.companyName || 'TaskkroW'}</div>
              <div>
                {user?.market === 'SK' ? 'Vystavený dňa' : 'Issued on'} {formatDate(issueDate, user?.market)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CreditNotePreview.displayName = 'CreditNotePreview';